/**
 * Numeric enums stored inside SimState. `const` objects + same-named type unions (no TS `enum`, so they survive
 * isolatedModules and are plain numbers for cloning/hashing). Never renumber; append.
 */

/** Player controller state machine (ARCHITECTURE §5 "Player controller"). Frame data lives in @hf/shared tuning.FRAMES. */
export const PlayerState = {
  /** Grounded, not moving (pre-snap stance included). */
  IDLE: 0,
  /** Grounded hop-run (or pre-snap shuffle). `hopPhase` drives the visual hop. */
  RUN: 1,
  /** Double-tap dash (MOVE.dashFrames). Can be cancelled into BACKSTEP (stutter) during FRAMES.stutter window. */
  DASH: 2,
  /** Stutter-step back-step after cancelling a dash. */
  BACKSTEP: 3,
  /** In the air from a jump (carrier jump = hurdle). `airFrame` counts frames since take-off. */
  AIRBORNE: 4,
  /** Landing lag. */
  LANDING: 5,
  /** Fake-jump cancel: 3 f freeze in the air… */
  FAKE_FREEZE: 6,
  /** …then a 2 f slam to the ground… */
  FAKE_SLAM: 7,
  /** …then an 8 f stumble (also used after whiffed lunges). */
  STUMBLE: 8,
  /** Carrier holding Down on the ground: crouched, charging a truck. */
  TRUCK_CHARGE: 9,
  /** Truck burst after release (low shoulder charge; beats Leap, loses to Dive). */
  TRUCK_BURST: 10,
  /** Spin move (i-frames during active). */
  SPIN: 11,
  /** Stiff-arm (Fist). */
  STIFF_ARM: 12,
  /** Dive grab (low). Startup can be cancelled into FAKE_DIVE with Down. */
  DIVE: 13,
  /** Fake-dive cancel recovery. */
  FAKE_DIVE: 14,
  /** Leap grab (high, airborne). */
  LEAP_GRAB: 15,
  /** QB holding Action: charging a throw. Down = pump fake. */
  CHARGING_THROW: 16,
  /** Throw release (ball launches on the last startup frame). */
  THROW_RELEASE: 17,
  /** Pump-fake recovery. */
  PUMP_FAKE: 18,
  /** Securing a catch. */
  CATCH: 19,
  /** Stunned (bulldozed, knocked aside by a truck, stiff-armed). */
  STUNNED: 20,
  /** Tackled / down. */
  TACKLED: 21,
  /** Celebration (score, big play). */
  CELEBRATE: 22,
  /** Pre-snap hard count (offense Jump): a short flinch that baits offside. */
  HARD_COUNT: 23,
  /** Posing during a special's telegraph (18 f). */
  SPECIAL_POSE: 24,
} as const;
export type PlayerState = (typeof PlayerState)[keyof typeof PlayerState];
export const PLAYER_STATE_NAMES: readonly string[] = Object.keys(PlayerState);

/** Glove poses (assets: design/assets/glove/glove-{open,fist,grip,ready,reach}[-away]-cut.png). */
export const GlovePose = {
  /** Catch / ready / swat. */
  OPEN: 0,
  /** Stiff-arm / punch-out. */
  FIST: 1,
  /** Holding / throwing the ball. */
  GRIP: 2,
  /** Neutral ready stance. */
  READY: 3,
  /** Stretched reach (lunges, catches at full extension). */
  REACH: 4,
} as const;
export type GlovePose = (typeof GlovePose)[keyof typeof GlovePose];

export const BallState = {
  /** Held by `ball.holder`. */
  HELD: 0,
  /** In flight after a throw or toss (also after a tip/swat — still catchable). */
  AIR: 1,
  /**
   * A KICK (Classic: kickoff / punt / onside toss, `ball.kick`) that hit the ground: still live. It bounces and
   * rolls; the receiver picks it up with any free glove (or his head) and returns it, the kicker only as a free ball
   * (kickoff / onside past the 10-yard line, or anything the receiver touched first) — a kicker touching an
   * untouched punt downs it. Dead KICK.looseDeadFrames after landing.
   */
  LOOSE: 2,
  /** Dead: the play is over. */
  DEAD: 3,
} as const;
export type BallState = (typeof BallState)[keyof typeof BallState];

/** Play flow (ARCHITECTURE §5 "Football rules"). */
export const Phase = {
  /** Line up; play clock runs; offense snaps with Action (Jump = hard count). */
  PRE_SNAP: 0,
  /** After the snap: the play is LIVE but the defender may not cross the LOS until the count ends (Blitz skips). */
  RUSH_COUNT: 1,
  /** Live play, no restrictions. */
  LIVE: 2,
  /** Dead ball: result banner and celebration; then the next down/possession is set up. */
  DEAD: 3,
  /** After a TD: the scorer picks 1-pt toss (Action) or 2-pt play (Up). */
  CONVERSION: 4,
  /** Match over. The sim keeps ticking but nothing changes except phaseFrame. */
  GAME_OVER: 5,
} as const;
export type Phase = (typeof Phase)[keyof typeof Phase];
export const PHASE_NAMES: readonly string[] = Object.keys(Phase);

export const PlayResultKind = {
  NONE: 0,
  /** Carrier tackled (gain or loss; see `yards`). */
  TACKLE: 1,
  INCOMPLETE: 2,
  TOUCHDOWN: 3,
  FIELD_GOAL_GOOD: 4,
  FIELD_GOAL_MISS: 5,
  INTERCEPTION: 6,
  /** Interception returned for a touchdown (scores for the defense). */
  PICK_SIX: 7,
  SAFETY: 8,
  SACK: 9,
  OFFSIDE: 10,
  TURNOVER_ON_DOWNS: 11,
  CONVERSION_GOOD: 12,
  CONVERSION_FAILED: 13,
  // ── Classic-format kicks (actor = the receiver unless noted; spotX = where the receiver's possession starts) ──
  /** Kick returner tackled / whistled (or downed in his own end zone: see TOUCHBACK). */
  KICK_RETURN: 14,
  /** Kick out of the back / dead in the receiver's end zone / returner down or kneeling there. */
  TOUCHBACK: 15,
  /** The KICKING player recovered a free kick (onside, a muffed kick); actor = the kicker; he keeps the ball. */
  KICK_RECOVERED: 16,
  /** The kick died untouched by the receiver (rested, downed by the kicker, timed out): receiver's ball there. */
  KICK_DOWNED: 17,
} as const;
export type PlayResultKind = (typeof PlayResultKind)[keyof typeof PlayResultKind];

export const ConversionChoice = { NONE: 0, PENDING: 1, TOSS: 2, TWO_POINT: 3 } as const;
export type ConversionChoice = (typeof ConversionChoice)[keyof typeof ConversionChoice];

/** What happens when the DEAD phase ends. */
export const AfterDead = {
  NEXT_DOWN: 0,
  /** Drive over: the other player gets the ball (or the match ends if all drives are used and scores differ). */
  NEXT_DRIVE: 1,
  /** Touchdown scored: go to CONVERSION. */
  CONVERSION: 2,
  /** Replay the down (offside). */
  REPLAY_DOWN: 3,
  GAME_OVER: 4,
  /** Classic: half-time pause (RULES.halftimeFrames) after HALF_END{half: 1}, then the second-half kickoff. */
  SECOND_HALF: 5,
} as const;
export type AfterDead = (typeof AfterDead)[keyof typeof AfterDead];

export const SpecialPhase = {
  /** Not active (can activate if power is full and not used this play). */
  IDLE: 0,
  /** 18 f telegraph: flash + sting; the opponent can still act. */
  TELEGRAPH: 1,
  /** Effect running (`timer` frames left, or armed until consumed). */
  ACTIVE: 2,
  /** Used this play. Resets to IDLE when the next play starts. */
  SPENT: 3,
  /**
   * Blitz only: armed SECRETLY in PRE_SNAP (no telegraph, power not yet spent). At the snap it becomes TELEGRAPH
   * (SnapEvent.blitz). HIDDEN INFORMATION: the HUD reports it as IDLE/ready, the renderer and audio show nothing,
   * and the AI must not read the opponent's ARMED phase.
   */
  ARMED: 4,
} as const;
export type SpecialPhase = (typeof SpecialPhase)[keyof typeof SpecialPhase];

/** Tackle kinds for events/announcer. */
export const TackleKind = { GRAB: 0, DIVE: 1, LEAP: 2 } as const;
export type TackleKind = (typeof TackleKind)[keyof typeof TackleKind];

/**
 * Camera focus target. SPLIT = centred between the real carrier and the Mirage decoy while `decoyActive`, so the
 * camera never gives the decoy away.
 */
export const CameraFocus = { BALL: 0, PLAYER0: 1, PLAYER1: 2, SPLIT: 3 } as const;
export type CameraFocus = (typeof CameraFocus)[keyof typeof CameraFocus];

/**
 * Declared play type (rules.playCall), chosen at the snap (ARCHITECTURE §5 "Field goals", "Classic format").
 *   NORMAL    run / pass. A NORMAL flight can never score a field goal.
 *   FG_TOSS   offense held ▼ while pressing Action to snap: FG formation. The throw uses `tossMul` and the flight
 *             gets `ball.fgAttempt`. Caught by the offense beyond the LOS = live pass (Fake FG).
 *   PAT_TOSS  1-pt conversion (forced by the conversion choice).
 *   KICKOFF   Classic: set at the kickoff line-up (opening kick, after scores, each half; the free kick after a
 *             safety). The kicker's Action press starts the play (no rush count), hold = charge, release = toss.
 *   ONSIDE    a KICKOFF released with ▼ held: the short skipping hop (set at the release).
 *   PUNT      Classic: offense held Special while pressing Action to snap ("special teams"), any down. The toss
 *             uses PUNT_CURVE; the punter may also run (fake punt).
 * Kick plays: `isKickCall`. A kick flight / loose kick has `ball.kick`.
 */
export const PlayCall = { NORMAL: 0, FG_TOSS: 1, PAT_TOSS: 2, KICKOFF: 3, PUNT: 4, ONSIDE: 5 } as const;
export type PlayCall = (typeof PlayCall)[keyof typeof PlayCall];
/** KICKOFF / ONSIDE / PUNT. */
export const isKickCall = (c: number): boolean =>
  c === PlayCall.KICKOFF || c === PlayCall.ONSIDE || c === PlayCall.PUNT;
/** KICKOFF / ONSIDE (free kicks: the kicker may recover past ONSIDE_MIN_YARDS). */
export const isFreeKickCall = (c: number): boolean => c === PlayCall.KICKOFF || c === PlayCall.ONSIDE;

/**
 * The most recent feint of a player (PlayerSim.feintKind, with feintTick). Bait rules per kind: ARCHITECTURE §5.
 * Carrier/QB feints: FAKE_JUMP, STUTTER, HESITATION, SPIN (dodge), PUMP_FAKE, HARD_COUNT, MIRAGE.
 * Defender feints: FAKE_DIVE, FAKE_RUSH (defender stutter), FAST_FALL.
 */
export const FeintKind = {
  NONE: 0,
  FAKE_JUMP: 1,
  STUTTER: 2,
  HESITATION: 3,
  SPIN: 4,
  PUMP_FAKE: 5,
  HARD_COUNT: 6,
  MIRAGE: 7,
  FAKE_DIVE: 8,
  FAKE_RUSH: 9,
  FAST_FALL: 10,
} as const;
export type FeintKind = (typeof FeintKind)[keyof typeof FeintKind];
/** FeintKind → FakeSuccessEvent.bait string (index = FeintKind). */
export const FEINT_BAIT_KEYS = [
  'none',
  'fake_jump',
  'stutter',
  'hesitation',
  'spin',
  'pump_fake',
  'hard_count',
  'mirage',
  'fake_dive',
  'fake_rush',
  'fast_fall',
] as const;
export type BaitKey = Exclude<(typeof FEINT_BAIT_KEYS)[number], 'none'>;

/** The most recent commitment of a player (PlayerSim.commitKind, with commitTick): what a feint can bait. */
export const CommitKind = {
  NONE: 0,
  /** Defender commitments (baited by carrier/QB feints). */
  DIVE: 1,
  LEAP: 2,
  /** Any take-off (a defender jumping a pump fake / hard count; an offense player jumping for a ball). */
  JUMP: 3,
  /** Carrier commitments (baited by defender feints). */
  HURDLE: 4,
  TRUCK: 5,
  SPIN: 6,
} as const;
export type CommitKind = (typeof CommitKind)[keyof typeof CommitKind];

/** Bits of PowerGainEvent.reasons (several reasons on one tick fold into one event). */
export const PowerReason = {
  YARDS: 1 << 0,
  TACKLE: 1 << 1,
  CATCH: 1 << 2,
  INTERCEPTION: 1 << 3,
  BAIT: 1 << 4,
  SWAT: 1 << 5,
} as const;
export type PowerReason = (typeof PowerReason)[keyof typeof PowerReason];

/** Indices into the fixed-length per-button arrays in InputHistory (same order as the InputFrame bits). */
export const BtnIndex = { L: 0, R: 1, U: 2, D: 3, A: 4, S: 5 } as const;
export type BtnIndex = (typeof BtnIndex)[keyof typeof BtnIndex];
export const BUTTON_COUNT = 6;
